import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { lumi } from '../lib/lumi';

export default function XinyiStorePage() {
  const location = useLocation();
  const [expandedFAQ, setExpandedFAQ] = React.useState<number | null>(null);
  const [expandedGuide, setExpandedGuide] = React.useState(false);
  const [expandedTraffic, setExpandedTraffic] = React.useState(false);
  const [homeCarouselPhotos, setHomeCarouselPhotos] = useState<string[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const carouselScrollEndTimerRef = useRef<number | null>(null);
  const autoplayPauseUntilRef = useRef(0);
  
  // 優先使用靜態頁面的全域變數，否則根據路徑判斷
  const currentLang = (window as any).__STATIC_LANG__ || 
    (location.pathname.startsWith('/en') ? 'en' : 'zh');
  
  // 多語系內容
  const content = {
    zh: {
      // Meta
      metaTitle: "信義區咖啡廳推薦｜ONGU Studio - 信義 職人手沖・現場烘豆咖啡",
      metaDescription: "信義區咖啡廳推薦 ONGU Studio - 信義，主打職人現場烘豆、淺培手沖與奶咖，鋁合金工業風設計，距離信義威秀400公尺，提供外帶與咖啡豆販售。",
      metaKeywords: "信義區咖啡廳, 信義區咖啡廳推薦, 信義區咖啡廳營業中, 信義區網美咖啡廳, 信義區不限時咖啡廳, 信義威秀咖啡, 信義區手沖咖啡, 信義區外帶咖啡",
      
      // Hero (One H1 per page: use sr-only H1 for SEO, visible hero uses <p>)
      seoH1: "信義區咖啡廳推薦｜ONGU Studio 信義｜自家烘焙・職人手沖",
      heroBrandTitle: "Ongu Studio - 信義",
      heroQuote: "「我們想在台北創造美好的咖啡日常。」",
      heroDesc: "專注於咖啡烘焙與沖煮，將熱情注入每一杯，讓停下腳步的片刻充滿放鬆與鼓舞。",
      
      // NON-Display 區塊
      nonDisplayTitle: "醞玖製研信義區咖啡廳與現場烘培工作室",
      nonDisplaySubtitle: "Ongu Studio -Coffee Roasting",
      nonDisplayContent: "研究，品質，烘焙與沖煮是為什麼我們可以信義區開咖啡廳以及在這個市中心做現場烘培屹立不搖的不二法門。無論手沖，冷萃，拿鐵，買現烘咖啡豆，品質都能兼顧。",
      
      // Section 1: 研究
      section1Tag: "Research",
      section1Title: "研究",
      section1Content: "我們在科學、自然與文化之間，持續探索咖啡的更多可能。\n以開放的視角看待咖啡產業，\n透過不斷的創造與學習，\n希望讓更多人產生深入理解與探索咖啡的動力。",
      
      // Section 2: 品質
      section2Tag: "Quality",
      section2Title: "品質",
      section2Content: "我們專注提供高品質、乾淨且甜度良好的咖啡豆\n減少烘焙賦予的風味，以平衡、柔和為主軸，保留產地特色，\n讓風味清晰、容易理解，也能自然融入日常。\n我們的烘焙不是為了追求極端表現，而是找到每支豆子最適合被呈現的樣貌。",
      
      // Section 3: 烘焙與沖煮
      section3Tag: "Roasting & Brewing",
      section3Title: "烘焙與沖煮",
      section3Content: "我們在不同品種、處理法與烘焙樣態之間反覆探索，也在沖煮中持續實驗與創新；\n透過水質、研磨、沖煮架構與萃取條件的調整，\n尋找最能展現柔和、平衡與乾淨結構的方式，\n讓每一杯咖啡，都帶著清楚的脈絡，輕柔地走進日常。",
      
      // 營業資訊
      infoTitle: "營業資訊",
      infoAddress: "ADDRESS",
      infoAddressValue: "台北市信義區信義路四段389號1樓",
      infoHours: "HOURS",
      infoHoursValue1: "週一至五：09:00–17:00",
      infoHoursValue2: "週六：10:00–17:00 ｜週日公休",
      infoHoursValue3: "",
      infoNearby: "NEARBY",
      infoNearbyValue: "距離信義威秀約 400 公尺（步行約5分鐘）",
      infoContact: "CONTACT",
      infoContactValue: "+886 938 510 307",
      infoInstagram: "INSTAGRAM",
      infoInstagramValue: "@onguroasters",
      
      // CTA
      ctaText: "ROASTING IN PROCESS, BREWING IN LIFE.",
      ctaCopyright: "© 2024 ONGU Studio. All rights reserved.",
      
      // 首屏資訊卡
      quickGuideTitle: "信義區咖啡快速指南",
      quickGuideStoreName: "店名",
      quickGuideStoreNameValue: "Ongu Studio -Coffee Roasting - 信義",
      quickGuidePosition: "定位",
      quickGuidePositionValue: "信義區咖啡廳推薦｜職人手沖・咖啡烘培",
      quickGuideFeatures: "特色",
      quickGuideFeaturesValue: "咖啡烘焙｜季節手沖｜全不鏽鋼工業設計｜座位精簡（適合外帶/短停品飲）",
      quickGuideFor: "適合誰",
      quickGuideForValue: "想找信義區手沖、信義區外帶咖啡、信義區自家烘焙咖啡豆的人",
      quickGuideInfo: "快速資訊",
      quickGuideCTA1: "選購咖啡豆",
      quickGuideCTA2: "選購濾掛/掛耳",
      quickGuideCTA3: "查看所有門市"
    },
    en: {
      metaTitle: "Best Coffee Shop in Xinyi District｜ONGU Studio Roastery & Lab｜Aluminum Design × On-site Roasting",
      metaDescription: "ONGU Studio in Taipei's Xinyi District blends KITBAY aluminum interiors with professional on-site coffee roasting. Known for its light roast and seasonal pour-overs, located just 500m from Taipei 101.",
      metaKeywords: "Xinyi coffee shop, Taipei coffee roastery, specialty coffee Taipei, light roast coffee, KITBAY design, aluminum interior cafe, Taipei 101 coffee, on-site roasting",
      
      seoH1: "Best coffee shop in Xinyi District｜ONGU Studio Xinyi｜House-roasted coffee & artisan pour-overs",
      heroBrandTitle: "Ongu Studio – Xinyi",
      heroQuote: "\"We want to create a beautiful coffee everyday in Taipei.\"",
      heroDesc: "Focused on coffee roasting and brewing, we pour our passion into every cup, making every pause filled with relaxation and uplift.",
      
      // NON-Display Section
      nonDisplayTitle: "Xinyi District Cafe & On-site Roasting Workshop",
      nonDisplaySubtitle: "Ongu Studio -Coffee Roasting",
      nonDisplayContent: "Research, quality, roasting, and brewing—these are the foundations that allow us to operate a cafe and maintain on-site roasting in the heart of Xinyi District.\nWhether pour-over, cold brew, latte, or fresh-roasted beans, quality is always our priority.",
      
      // Section 1: Research
      section1Tag: "Research",
      section1Title: "Research",
      section1Content: "Between science, nature, and culture, we continuously explore the endless possibilities of coffee.\nWith an open perspective on the coffee industry,\nthrough constant creation and learning,\nwe hope to inspire more people to deeply understand and explore coffee.",
      
      // Section 2: Quality
      section2Tag: "Quality",
      section2Title: "Quality",
      section2Content: "We focus on providing high-quality, clean coffee beans with excellent sweetness.\nReducing roast-imparted flavors, we emphasize balance and gentleness while preserving origin characteristics,\nmaking flavors clear, easy to understand, and naturally integrated into daily life.\nOur roasting is not about pursuing extremes, but finding the most suitable expression for each bean.",
      
      // Section 3: Roasting & Brewing
      section3Tag: "Roasting & Brewing",
      section3Title: "Roasting & Brewing",
      section3Content: "We continuously explore different varieties, processing methods, and roasting profiles while experimenting and innovating in brewing.\nThrough adjustments in water quality, grind size, brewing structure, and extraction conditions,\nwe seek methods that best showcase gentle, balanced, and clean characteristics,\nallowing every cup to carry a clear narrative and gently enter daily life.",
      
      infoTitle: "Visit Us",
      infoAddress: "ADDRESS",
      infoAddressValue: "1F, No.389, Sec.4, Xinyi Rd., Xinyi District, Taipei, Taiwan",
      infoHours: "HOURS",
      infoHoursValue1: "Mon–Fri: 09:00–17:00",
      infoHoursValue2: "Sat: 10:00–17:00 ｜ Sun: Closed",
      infoHoursValue3: "",
      infoNearby: "NEARBY",
      infoNearbyValue: "Approx. 400m from Vieshow Cinemas (5-min walk)",
      infoContact: "CONTACT",
      infoContactValue: "+886 938 510 307",
      infoInstagram: "INSTAGRAM",
      infoInstagramValue: "@onguroasters",
      
      ctaText: "ROASTING IN PROCESS, BREWING IN LIFE.",
      ctaCopyright: "© 2024 ONGU Studio. All rights reserved.",
      
      quickGuideTitle: "Xinyi Coffee Quick Guide",
      quickGuideStoreName: "Store Name",
      quickGuideStoreNameValue: "Ongu Studio -Coffee Roasting - Xinyi",
      quickGuidePosition: "Positioning",
      quickGuidePositionValue: "Xinyi Specialty Coffee｜Artisan Pour-over & Coffee Roasting",
      quickGuideFeatures: "Features",
      quickGuideFeaturesValue: "Coffee Roasting｜Seasonal Pour-over｜Full Stainless Steel Industrial Design｜Limited Seating (Ideal for takeaway/brief tasting)",
      quickGuideFor: "Perfect For",
      quickGuideForValue: "Those seeking pour-over coffee, takeaway coffee, or house-roasted beans in Xinyi District",
      quickGuideInfo: "Quick Info",
      quickGuideCTA1: "Shop Coffee Beans",
      quickGuideCTA2: "Shop Drip Bags",
      quickGuideCTA3: "View All Locations"
    }
  };

  const t = content[currentLang];

  // 獲取首頁輪播圖照片
  useEffect(() => {
    const fetchCarouselPhotos = async () => {
      try {
        const { list } = await lumi.entities.photos.list({
          sort: { updatedAt: -1 },
          limit: 100
        });

        const urls = (list as any[])
          .filter(p => Array.isArray(p?.displayLocations) && p.displayLocations.includes('homeCarousel'))
          .map(p => p?.fileUrl)
          .filter(Boolean);

        setHomeCarouselPhotos(urls);
        setCarouselIndex(0);
      } catch (error) {
        console.error('Failed to fetch carousel photos:', error);
        setHomeCarouselPhotos([]);
      }
    };

    fetchCarouselPhotos();
  }, []);

  // 輪播圖資料源（帶 fallback）
  const carouselFallback = useMemo(
    () => [
      'https://static.lumi.new/8b/8b07e4525d7ce64857cde49209ceeeae.webp',
      'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg',
      'https://images.pexels.com/photos/1695052/pexels-photo-1695052.jpeg',
    ],
    []
  );

  const carouselSource = useMemo(() => {
    const src = homeCarouselPhotos.length > 0 ? homeCarouselPhotos : carouselFallback;
    return src.filter(Boolean);
  }, [homeCarouselPhotos, carouselFallback]);

  const scrollCarouselToIndex = (idx: number) => {
    const el = carouselRef.current;
    const children = el?.children;
    const target = children?.item(idx) as HTMLElement | null;
    if (!el || !target) return;

    el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  };

  // 自動輪播
  useEffect(() => {
    const total = carouselSource.length;
    if (total <= 1) return;

    const id = window.setInterval(() => {
      if (Date.now() < autoplayPauseUntilRef.current) return;

      setCarouselIndex(prev => {
        const next = (prev + 1) % total;
        requestAnimationFrame(() => scrollCarouselToIndex(next));
        return next;
      });
    }, 4500);

    return () => window.clearInterval(id);
  }, [carouselSource.length]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Helmet>
        {/* ... keep existing code */}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDescription} />
        {currentLang === 'zh' && <meta name="keywords" content={t.metaKeywords} />}
        
        <link rel="canonical" href={`https://www.ongucoffee.com/${currentLang}/store/xinyi`} />
        <link rel="alternate" hrefLang="zh-Hant" href="https://www.ongucoffee.com/zh/store/xinyi" />
        <link rel="alternate" hrefLang="en" href="https://www.ongucoffee.com/en/store/xinyi" />
        <link rel="alternate" hrefLang="x-default" href="https://www.ongucoffee.com/en/store/xinyi" />
        
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={currentLang === 'zh' ? 'zh_TW' : 'en_US'} />
        <meta property="og:site_name" content="ONGU Studio - 信義" />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDescription} />
        <meta property="og:url" content={`https://www.ongucoffee.com/${currentLang}/store/xinyi`} />
        <meta property="og:image" content="https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.metaTitle} />
        <meta name="twitter:description" content={t.metaDescription} />
        <meta name="twitter:image" content="https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg" />
        
        <link rel="icon" href="https://static.lumi.new/f4/f41cec5bb6b72110e177830b0f994a38.png" type="image/png" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "CafeOrCoffeeShop",
                "@id": "https://www.ongucoffee.com/zh/store/xinyi#business",
                "name": "ONGU Studio - 信義",
                "image": [
                  "https://file.lumi.new/p373839333138763776/SPF1986470447160213505.jpg",
                  "https://file.lumi.new/p373839333138763776/SPF1986470447156019200.jpg",
                  "https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg"
                ],
                "url": "https://www.ongucoffee.com/zh/store/xinyi",
                "description": t.metaDescription,
                "priceRange": "$$",
                "telephone": "+886938510307",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "台北市信義區信義路四段389號1樓",
                  "addressLocality": "台北市",
                  "addressRegion": "信義區",
                  "postalCode": "110",
                  "addressCountry": "TW"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.033,
                  "longitude": 121.565
                },
                "openingHoursSpecification": [
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Tuesday","Wednesday","Thursday","Friday"],
                    "opens": "09:00",
                    "closes": "17:00"
                  },
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Saturday","Sunday"],
                    "opens": "10:00",
                    "closes": "17:00"
                  }
                ],
                "servesCuisine": "Coffee",
                "menu": "https://www.ongucoffee.com/zh/menu",
                "hasMap": "https://www.google.com/maps/search/?api=1&query=25.033%2C121.565",
                "sameAs": [
                  "https://www.instagram.com/onguroasters/",
                  "https://www.facebook.com/ongustudio?locale=zh_TW"
                ],
                "brand": {
                  "@type": "Brand",
                  "name": "ONGU Studio",
                  "url": "https://www.ongucoffee.com/"
                }
              },
              {
                "@type": "WebPage",
                "@id": "https://www.ongucoffee.com/zh/store/xinyi#webpage",
                "url": "https://www.ongucoffee.com/zh/store/xinyi",
                "name": "ONGU Studio - 信義｜信義區咖啡廳推薦、職人手沖與現場烘豆體驗",
                "inLanguage": currentLang === 'zh' ? 'zh-TW' : 'en',
                "isPartOf": {
                  "@id": "https://www.ongucoffee.com#website"
                },
                "about": {
                  "@id": "https://www.ongucoffee.com/zh/store/xinyi#business"
                },
                "primaryImageOfPage": {
                  "@id": "https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg"
                },
                "description": t.metaDescription
              },
              {
                "@type": "FAQPage",
                "@id": "https://www.ongucoffee.com/zh/store/xinyi#faq",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "ONGU Studio 信義店算信義區咖啡廳推薦嗎？特色是什麼？",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "是的，ONGU Studio 信義店主打「職人現場烘豆＋季節手沖」與金屬工業設計空間。座位不多，但能近距離看見製作過程，適合想在信義區喝到乾淨、層次清晰風味的人。"
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "這裡比較適合外帶還是內用？有座位嗎？",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "店內座位精簡，保留寬敞的職人操作區與展示區。你可以內用短停品飲，也很適合在信義區外帶咖啡、順路帶走一杯手沖或奶咖。"
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "信義店有現場烘豆嗎？每天都有嗎？",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "有的，ONGU 信義店以現場烘豆為核心特色之一。烘豆師會監控溫度、時間、氣流與顏色變化，讓你能聞到烘焙過程的香氣與節奏。更多常見問題請參考完整 FAQ 頁面：https://www.ongucoffee.com/zh/faq"
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section - 依照片原始比例顯示 */}
      <section className="relative overflow-hidden">
        <h1 className="sr-only">{t.seoH1}</h1>

        <div className="relative">
          <img 
            src="https://file.lumi.new/p373839333138763776/SPF2017537549456433152.webp" 
            alt="ONGU Studio 信義店"
            className="w-full h-auto"
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>
      </section>

      {/* Brand Philosophy Section - 品牌理念 */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light tracking-wider text-gray-800 dark:text-gray-200 mb-8">
              {t.heroBrandTitle}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
              {t.heroQuote}

{t.heroDesc}
            </p>
          </div>
        </div>
      </section>



      {/* Photo Carousel Section - 與首頁同步 */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="relative">
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none]"
              style={{ WebkitOverflowScrolling: 'touch' }}
              onScroll={() => {
                autoplayPauseUntilRef.current = Date.now() + 5000;

                if (carouselScrollEndTimerRef.current) {
                  window.clearTimeout(carouselScrollEndTimerRef.current);
                }

                carouselScrollEndTimerRef.current = window.setTimeout(() => {
                  const el = carouselRef.current;
                  if (!el) return;

                  const children = Array.from(el.children) as HTMLElement[];
                  if (!children.length) return;

                  const left = el.scrollLeft;
                  let bestIdx = 0;
                  let bestDist = Number.POSITIVE_INFINITY;

                  for (let i = 0; i < children.length; i++) {
                    const dist = Math.abs(children[i].offsetLeft - left);
                    if (dist < bestDist) {
                      bestDist = dist;
                      bestIdx = i;
                    }
                  }

                  setCarouselIndex(bestIdx);
                }, 120);
              }}
            >
              {carouselSource.map((url, i) => (
                <div
                  key={`${url}-${i}`}
                  className="flex-shrink-0 w-[90%] md:w-[calc(33.333%-11px)] snap-start"
                >
                  <img
                    src={url}
                    alt={`Carousel ${i + 1}`}
                    className="w-full aspect-[488/605] object-cover"
                    draggable={false}
                  />
                </div>
              ))}
            </div>

            {/* 隱藏 WebKit scrollbar */}
            <style>{`
              .snap-x::-webkit-scrollbar { display: none; }
            `}</style>
          </div>
        </div>
      </section>

      {/* Section 1: 研究 */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="order-2 md:order-1">
              <div className="aspect-square bg-zinc-100 dark:bg-zinc-900 rounded-sm overflow-hidden">
                <img 
                  src="https://file.lumi.new/p373839333138763776/SPF2021991565719232512.webp" 
                  alt="咖啡研究"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="order-1 md:order-2 space-y-6">
              <div className="inline-block">
                <span className="text-xs tracking-[0.3em] text-zinc-500 dark:text-zinc-500 uppercase">/ {t.section1Tag} /</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-light text-zinc-900 dark:text-zinc-100 tracking-wide leading-tight">
                {t.section1Title}
              </h2>
              <div className="w-16 h-[1px] bg-zinc-400 dark:bg-zinc-600"></div>
              <p className="text-sm md:text-base font-light text-zinc-700 dark:text-zinc-400 leading-loose tracking-wide whitespace-pre-line">
                {t.section1Content}
              </p>
            </div>
          </div>
        </div>
      </section>




      {/* Section 2: 品質 */}
      <section className="py-24 bg-zinc-50 dark:bg-zinc-950">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="text-xs tracking-[0.3em] text-zinc-500 dark:text-zinc-500 uppercase">/ {t.section2Tag} /</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-light text-zinc-900 dark:text-zinc-100 tracking-wide leading-tight">
                {t.section2Title}
              </h2>
              <div className="w-16 h-[1px] bg-zinc-400 dark:bg-zinc-600"></div>
              <p className="text-sm md:text-base font-light text-zinc-700 dark:text-zinc-400 leading-loose tracking-wide whitespace-pre-line">
                {t.section2Content}
              </p>
            </div>

            <div className="order-1 md:order-2">
              <div className="aspect-square bg-zinc-100 dark:bg-zinc-900 rounded-sm overflow-hidden">
                <img 
                  src="https://file.lumi.new/p373839333138763776/SPF2021991565232693248.webp" 
                  alt="咖啡吧台品質展示"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: 烘焙與沖煮 */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="order-2 md:order-1">
              <div className="aspect-square bg-zinc-100 dark:bg-zinc-900 rounded-sm overflow-hidden">
                <img 
                  src="https://file.lumi.new/p373839333138763776/SPF2021991564637102080.webp" 
                  alt="手沖咖啡過程"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="order-1 md:order-2 space-y-6">
              <div className="inline-block">
                <span className="text-xs tracking-[0.3em] text-zinc-500 dark:text-zinc-500 uppercase">/ {t.section3Tag} /</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-light text-zinc-900 dark:text-zinc-100 tracking-wide leading-tight">
                {t.section3Title}
              </h2>
              <div className="w-16 h-[1px] bg-zinc-400 dark:bg-zinc-600"></div>
              <p className="text-sm md:text-base font-light text-zinc-700 dark:text-zinc-400 leading-loose tracking-wide whitespace-pre-line">
                {t.section3Content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 首屏資訊卡｜信義區咖啡快速指南 */}
      <section className="py-16 bg-zinc-50 dark:bg-zinc-950">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-extralight text-zinc-900 dark:text-zinc-100 tracking-wide">
                {t.quickGuideTitle}
              </h2>
              <div className="w-16 h-[1px] bg-zinc-400 dark:bg-zinc-600 mx-auto mt-4"></div>
            </div>

            <div className="border border-zinc-200 dark:border-zinc-800">
              <button
                onClick={() => setExpandedGuide(!expandedGuide)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
              >
                <span className="text-sm font-light tracking-wider text-zinc-900 dark:text-zinc-100">
                  Ongu Studio -Coffee Roasting - 信義
                </span>
                <span className="text-zinc-400 text-xl">{expandedGuide ? '−' : '+'}</span>
              </button>
              
              {expandedGuide && (
                <div className="border-t border-zinc-200 dark:border-zinc-800">
                  <div className="bg-white dark:bg-gray-900 p-8 md:p-12">
                    <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-light text-zinc-500 dark:text-zinc-500 tracking-wider mb-2">{t.quickGuideStoreName}</p>
                    <a
                      href="https://maps.app.goo.gl/miaiuCniLAJrPCGZ6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-light text-zinc-900 dark:text-zinc-100 tracking-wide underline underline-offset-4 hover:text-zinc-600 dark:hover:text-zinc-400 transition-colors"
                    >
                      {t.quickGuideStoreNameValue}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs font-light text-zinc-500 dark:text-zinc-500 tracking-wider mb-2">{t.quickGuidePosition}</p>
                    <p className="text-base font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                      {t.quickGuidePositionValue}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-light text-zinc-500 dark:text-zinc-500 tracking-wider mb-2">{t.quickGuideFeatures}</p>
                  <p className="text-base font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                    {t.quickGuideFeaturesValue}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-light text-zinc-500 dark:text-zinc-500 tracking-wider mb-2">{t.quickGuideFor}</p>
                  <p className="text-base font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                    {t.quickGuideForValue}
                  </p>
                </div>

                <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                  <p className="text-xs font-light text-zinc-500 dark:text-zinc-500 tracking-wider mb-4">{t.quickGuideInfo}</p>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="text-lg">📍</span>
                      <div>
                        <a
                          href="https://maps.app.goo.gl/miaiuCniLAJrPCGZ6"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-light text-zinc-900 dark:text-zinc-100 tracking-wide underline underline-offset-4 hover:text-zinc-600 dark:hover:text-zinc-400 transition-colors"
                        >
                          {t.infoAddressValue}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-lg">🕒</span>
                      <div>
                        <p className="text-sm font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                          {t.infoHoursValue1}
                          {currentLang === 'zh' && ` | ${t.infoHoursValue2} | ${t.infoHoursValue3}`}
                        </p>
                        {currentLang === 'en' && t.infoHoursValue2 && (
                          <p className="text-sm font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                            {t.infoHoursValue2}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-lg">☎</span>
                      <div>
                        <p className="text-sm font-light text-zinc-900 dark:text-zinc-100 tracking-wide">
                          {t.infoContactValue}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mt-8">
              <a 
                href={`/${currentLang}/shop`}
                className="block text-center py-4 px-6 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-light tracking-wider hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors rounded-sm"
              >
                {t.quickGuideCTA1}
              </a>
              <a 
                href={`/${currentLang}/shop`}
                className="block text-center py-4 px-6 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-light tracking-wider hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors rounded-sm"
              >
                {t.quickGuideCTA2}
              </a>
              <a 
                href={`/${currentLang}/store`}
                className="block text-center py-4 px-6 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-light tracking-wider hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors rounded-sm"
              >
                {t.quickGuideCTA3}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO GET HERE - 交通資訊 */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-extralight text-zinc-900 dark:text-zinc-100 tracking-wide">
                {currentLang === 'zh' ? 'HOW TO GET HERE' : 'HOW TO GET HERE'}
              </h2>
              <div className="w-16 h-[1px] bg-zinc-400 dark:bg-zinc-600 mx-auto mt-4"></div>
            </div>

            <div className="border border-zinc-200 dark:border-zinc-800">
              <button
                onClick={() => setExpandedTraffic(!expandedTraffic)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
              >
                <span className="text-sm font-light tracking-wider text-zinc-900 dark:text-zinc-100">
                  {currentLang === 'zh' 
                    ? '交通指引｜從台北 101／信義商圈怎麼到 ONGU Studio 信義店'
                    : 'How to Get Here｜From Taipei 101 / Xinyi District to ONGU Studio Xinyi'}
                </span>
                <span className="text-zinc-400 text-xl">{expandedTraffic ? '−' : '+'}</span>
              </button>
              
              {expandedTraffic && (
                <div className="border-t border-zinc-200 dark:border-zinc-800">
                  <div className="bg-white dark:bg-gray-900 p-8 md:p-12">
                    <div className="space-y-6 text-sm md:text-base font-light text-zinc-700 dark:text-zinc-400 leading-loose tracking-wide mb-8">
                      <p>
                        {currentLang === 'zh' ? (
                          'ONGU Studio 位於台北市信義區信義路四段389號，屬於信義商圈步行範圍。'
                        ) : (
                          'ONGU Studio is located at No. 389, Sec. 4, Xinyi Road, Xinyi District, Taipei, within walking distance of the Xinyi shopping district.'
                        )}
                      </p>
                      <p>
                        {currentLang === 'zh' ? (
                          '若你在台北 101 或信義威秀一帶，沿著信義路步行前往，約 5–8 分鐘即可抵達。'
                        ) : (
                          'If you are around Taipei 101 or Vieshow Cinemas, walk along Xinyi Road and you can arrive in about 5–8 minutes.'
                        )}
                      </p>
                      <p>
                        {currentLang === 'zh' ? (
                          '從信義威秀步行約 5 分鐘，距離台北 101 約 500 公尺左右，很適合你在信義商圈逛街、辦公途中，順路外帶一杯咖啡或停下來喝一杯季節手沖。'
                        ) : (
                          'About 5 minutes walk from Vieshow Cinemas, and around 500 meters from Taipei 101—perfect for grabbing a takeaway coffee or stopping by for a seasonal pour-over while shopping or working in the Xinyi area.'
                        )}
                      </p>
                    </div>

                    {/* 導航按鈕 */}
                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                      <a
                        href="https://maps.app.goo.gl/miaiuCniLAJrPCGZ6"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 py-4 px-6 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-light tracking-wide hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors rounded-sm"
                      >
                        <span>📍</span>
                        <span>{currentLang === 'zh' ? 'Google 地圖一鍵開啟' : 'Open in Google Maps'}</span>
                      </a>
                      <a
                        href="https://www.google.com/maps/dir/台北101/25.033,121.565"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-4 px-6 border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light tracking-wide text-center hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors rounded-sm"
                      >
                        {currentLang === 'zh' ? '從台北 101 導航到這裡' : 'Navigate from Taipei 101'}
                      </a>
                      <a
                        href="https://www.google.com/maps/dir/信義威秀/25.033,121.565"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-4 px-6 border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light tracking-wide text-center hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors rounded-sm"
                      >
                        {currentLang === 'zh' ? '從信義威秀導航到這裡' : 'Navigate from Vieshow Cinemas'}
                      </a>
                    </div>

                    {/* 底部提示 */}
                    <p className="text-center text-sm font-light text-zinc-600 dark:text-zinc-500 mb-8">
                      {currentLang === 'zh' ? (
                        <>如果你正在找其他門市，也可以先看 <a href={`/${currentLang}/store`} className="underline underline-offset-4 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors">門市總覽</a> 再安排前路開車行程。</>
                      ) : (
                        <>If you're looking for other locations, check our <a href={`/${currentLang}/store`} className="underline underline-offset-4 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors">Store Overview</a> to plan your visit.</>
                      )}
                    </p>

                    {/* Google Maps 嵌入 */}
                    <div className="aspect-video bg-gradient-to-br from-zinc-200 to-zinc-300 dark:from-zinc-800 dark:to-zinc-900 rounded-sm overflow-hidden shadow-2xl">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3615.0234567890123!2d121.56300000000001!3d25.033!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDAxJzU4LjgiTiAxMjHCsDMzJzU0LjAiRQ!5e0!3m2!1sen!2stw!4v1234567890123!5m2!1sen!2stw"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="ONGU Studio 信義店地圖"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-zinc-50 dark:bg-zinc-950" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extralight text-zinc-900 dark:text-zinc-100 tracking-wide mb-4">
                FAQ
              </h2>
              <div className="w-16 h-[1px] bg-zinc-400 dark:bg-zinc-600 mx-auto"></div>
            </div>

            <div className="space-y-4">
              {/* Question 1 */}
              <div className="border border-zinc-200 dark:border-zinc-800" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === 1 ? null : 1)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                >
                  <span className="text-sm font-light tracking-wider text-zinc-900 dark:text-zinc-100">
                    QUESTION 1
                  </span>
                  <span className="text-zinc-400 text-xl">{expandedFAQ === 1 ? '−' : '+'}</span>
                </button>
                {expandedFAQ === 1 && (
                  <div className="border-t border-zinc-200 dark:border-zinc-800 px-6 py-6" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-xs font-light tracking-wide text-zinc-500 dark:text-zinc-500 mb-3" itemProp="name">
                      ONGU Studio 信義店算信義區咖啡廳推薦嗎？特色是什麼？
                    </p>
                    <p className="text-sm font-light text-zinc-700 dark:text-zinc-400 leading-loose" itemProp="text">
                      是的，ONGU Studio 信義店主打「職人現場烘豆＋季節手沖」與金屬工業設計空間。座位不多，但能近距離看見製作過程，適合想在信義區喝到乾淨、層次清晰風味的人。
                    </p>
                  </div>
                )}
              </div>

              {/* Question 2 */}
              <div className="border border-zinc-200 dark:border-zinc-800" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === 2 ? null : 2)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                >
                  <span className="text-sm font-light tracking-wider text-zinc-900 dark:text-zinc-100">
                    QUESTION 2
                  </span>
                  <span className="text-zinc-400 text-xl">{expandedFAQ === 2 ? '−' : '+'}</span>
                </button>
                {expandedFAQ === 2 && (
                  <div className="border-t border-zinc-200 dark:border-zinc-800 px-6 py-6" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-xs font-light tracking-wide text-zinc-500 dark:text-zinc-500 mb-3" itemProp="name">
                      這裡比較適合外帶還是內用？有座位嗎？
                    </p>
                    <p className="text-sm font-light text-zinc-700 dark:text-zinc-400 leading-loose" itemProp="text">
                      店內座位精簡，保留寬敞的職人操作區與展示區。你可以內用短停品飲，也很適合在信義區外帶咖啡、順路帶走一杯手沖或奶咖。
                    </p>
                  </div>
                )}
              </div>

              {/* Question 3 */}
              <div className="border border-zinc-200 dark:border-zinc-800" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === 3 ? null : 3)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                >
                  <span className="text-sm font-light tracking-wider text-zinc-900 dark:text-zinc-100">
                    QUESTION 3
                  </span>
                  <span className="text-zinc-400 text-xl">{expandedFAQ === 3 ? '−' : '+'}</span>
                </button>
                {expandedFAQ === 3 && (
                  <div className="border-t border-zinc-200 dark:border-zinc-800 px-6 py-6" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-xs font-light tracking-wide text-zinc-500 dark:text-zinc-500 mb-3" itemProp="name">
                      信義店有現場烘豆嗎？每天都有嗎？
                    </p>
                    <p className="text-sm font-light text-zinc-700 dark:text-zinc-400 leading-loose" itemProp="text">
                      有的，ONGU 信義店以現場烘豆為核心特色之一。烘豆師會監控溫度、時間、氣流與顏色變化，讓你能聞到烘焙過程的香氣與節奏。
                    </p>
                  </div>
                )}
              </div>

              {/* View Full FAQ Button */}
              <div className="border border-zinc-200 dark:border-zinc-800">
                <a
                  href={`/${currentLang}/faq`}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors block"
                >
                  <span className="text-sm font-light tracking-wider text-zinc-900 dark:text-zinc-100">
                    查看完整FAQ
                  </span>
                  <span className="text-zinc-400 text-xl">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 底部 CTA */}
      <section className="py-16 bg-zinc-900 dark:bg-black">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] text-center">
          <p className="text-zinc-400 text-sm tracking-[0.3em] mb-4">{t.ctaText}</p>
          <p className="text-zinc-600 text-xs tracking-wider">
            {t.ctaCopyright}
          </p>
        </div>
      </section>
    </div>
  );
}
